import * as React from "react";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { type ReportGroupData, type ReportTableResults } from "./ReportTable";
import ReportTableBody from "./ReportTableBody";
import ReportTableFooter from "./ReportTableFooter";
import ReportTableHeader from "./ReportTableHeader";

interface Props {
    columns: Array<string>;
    renderHeader?: (column: string) => React.ReactNode;
    data: Array<ReportTableResults>;
    complete: boolean;
    loading: boolean;
    groups: ReportGroupData;
    parentElement?: HTMLElement;
    nullName?: string;
    totals: Record<string, string | null>;
    maxDepth: number;
    widest?: Record<string, string>;
    onLoad: () => Promise<void>;
    onGroupClick: (groupIndex: Array<string>) => void;
    layers?: number;
    nullGroupNames: Array<string>;
    topPadding?: number;
    height?: number;
}

interface State {
    showLoading: boolean;
}

export default class ReportTableRenderer extends React.PureComponent<Props, State> {
    public static defaultProps = {
        loading : false,
        maxDepth: 0,
    };

    public state: State = {
        showLoading: this.props.loading || !this.props.columns.length,
    };

    protected loadingTimer: ReturnType<typeof setTimeout> | undefined;

    public componentDidUpdate(): void {
        if(this.props.loading || !this.props.columns.length) {
            if(this.loadingTimer) {
                clearTimeout(this.loadingTimer);
            }

            this.setState({
                showLoading: true,
            });
        } else if(this.state.showLoading && typeof this.loadingTimer === "undefined") {
            setTimeout(() => {
                this.setState({
                    showLoading: false,
                });
            }, 100);
        }
    }

    public render(): React.ReactNode {
        const columnClasses = this.props.columns.reduce((map, column) => {
            const split = column.toString().split(" ").map(item => item.toLowerCase());

            map[column] = split.join("-");

            return map;
        }, {} as Record<string, string>);

        return (
            <>
                <table className="report-table">
                    <ReportTableHeader
                        columns={this.props.columns}
                        columnClasses={columnClasses}
                        renderHeader={this.props.renderHeader}
                    />
                    <ReportTableBody
                        maxDepth={this.props.maxDepth}
                        widest={this.props.widest}
                        data={this.props.data}
                        columns={this.props.columns}
                        onLoad={this.props.onLoad}
                        complete={this.props.complete || this.props.loading}
                        groups={this.props.groups}
                        parentElement={this.props.parentElement}
                        nullName={this.props.nullName}
                        onGroupClick={this.props.onGroupClick}
                        columnClasses={columnClasses}
                        layers={this.props.layers}
                        nullGroupNames={this.props.nullGroupNames}
                        loading={this.props.loading}
                        topPadding={this.props.topPadding}
                        height={this.props.height}
                    />
                    <ReportTableFooter
                        columns={this.props.columns}
                        totals={this.props.totals}
                        columnClasses={columnClasses}
                    />
                </table>
                {this.state.showLoading && (
                    <div className="report-table-loading">
                        <div className="report-table-loading-sticky">
                            <FontAwesomeLight icon="spinner-third" spin />
                            {this.props.columns.length !== 0 && (
                                <p>Loading...</p>
                            )}
                        </div>
                    </div>
                )}
            </>
        );
    }
}
